import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { ClickOutsideDirective } from '../../../shared/directives/click-outside.directive';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { type AppLanguageCode, type AppLanguageOption } from '../../../core/models/app-language.model';
import { AuthService } from '../../../core/services/auth.service';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, ClickOutsideDirective, TranslatePipe],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'app-navbar-host'
  }
})
export class NavbarComponent {
  private readonly authService = inject(AuthService);

  readonly appName = input.required<string>();
  readonly moduleName = input.required<string>();
  readonly moduleBadge = input('Module 1');
  readonly logoutLink = input('/');
  readonly darkMode = input(false);
  readonly language = input.required<AppLanguageCode>();
  readonly languages = input.required<readonly AppLanguageOption[]>();

  readonly themeToggle = output<void>();
  readonly languageChange = output<AppLanguageCode>();
  protected languageMenuOpen = false;
  protected profileMenuOpen = false;

  protected readonly selectedLanguage = computed(
    () => this.languages().find((item) => item.value === this.language()) ?? this.languages()[0]
  );
  protected readonly showAdminModule = computed(() => this.authService.isAdmin());
  protected readonly profileEmail = computed(() => {
    const username = this.authService.session()?.username?.trim();
    return username ? `${username}@vste.cz` : 'user@vste.cz';
  });

  protected onThemeToggle(): void {
    this.themeToggle.emit();
  }

  protected toggleLanguageMenu(): void {
    this.profileMenuOpen = false;
    this.languageMenuOpen = !this.languageMenuOpen;
  }

  protected closeLanguageMenu(): void {
    this.languageMenuOpen = false;
  }

  protected toggleProfileMenu(): void {
    this.languageMenuOpen = false;
    this.profileMenuOpen = !this.profileMenuOpen;
  }

  protected closeProfileMenu(): void {
    this.profileMenuOpen = false;
  }

  protected selectLanguage(nextLanguage: AppLanguageCode): void {
    this.languageMenuOpen = false;
    this.languageChange.emit(nextLanguage);
  }

  protected logout(): void {
    this.profileMenuOpen = false;
    this.authService.logout();
  }
}
